import { reactive } from 'vue'
import pin2d8cf0 from '/@/assets/icons/pin-2d8cf0.svg'
import { MapDoodleType } from '/@/constants/map'
import { getRoot } from '/@/root'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'
import Draw, { createBox, createRegularPolygon } from 'ol/interaction/Draw'
import Modify from 'ol/interaction/Modify'
import Snap from 'ol/interaction/Snap'
import DragPan from 'ol/interaction/DragPan'
import CircleStyle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import { Polygon, Point, LineString } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import arrowIcon from '/@/assets/icons/arrow-up-circle.svg'
import { Icon, Style, Stroke, Text } from 'ol/style'
import { UseLawnmower } from './use-lawnmower'
import { buildDock2MissionKmz } from './use-kml-generator'
import RegularShape from 'ol/style/RegularShape'

export function useMouseTool () {
  const root = getRoot()
  const useLawnmower = UseLawnmower()

  const state = reactive({
    pinNum: 0,
    polylineNum: 0,
    PolygonNum: 0,
    currentType: '',
    draw: null,
    boundaryModify: null,
    directionArrowModify: null,
    snap: null,
    routeBearingData: null,
    routeLayer: null,
    arrowLayer: null,
    polygonFeature: null,
    directionDeg: 0,
    bearingDeg: 0,
    flightAltitude: 20,
    flightHeightMode: 'aboveGroundLevel', // relativeToStartPoint
    directionArrowLength: 0
  })

  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }

  // Preserve arrow length when polyline moves
  function updateArrowFromCenter (center, angle, length) {
    return [
      center,
      [
        center[0] + Math.cos(angle) * length,
        center[1] + Math.sin(angle) * length
      ]
    ]
  }

  function lineIntersection (p1:number[], p2:number[], p3:number[], p4:number[]) {
    const den =
      (p1[0] - p2[0]) * (p3[1] - p4[1]) -
      (p1[1] - p2[1]) * (p3[0] - p4[0])

    if (den === 0) return null

    const x =
      ((p1[0] * p2[1] - p1[1] * p2[0]) * (p3[0] - p4[0]) -
      (p1[0] - p2[0]) * (p3[0] * p4[1] - p3[1] * p4[0])) / den

    const y =
      ((p1[0] * p2[1] - p1[1] * p2[0]) * (p3[1] - p4[1]) -
      (p1[1] - p2[1]) * (p3[0] * p4[1] - p3[1] * p4[0])) / den

    return [x, y]
  }

  function createArrowPoints (start:number[], angleRad:number, length:number) {
    const end = [
      start[0] + Math.cos(angleRad) * length,
      start[1] + Math.sin(angleRad) * length
    ]

    return [start, end]
  }

  function drawArrowFeature (start:number[], end:number[]) {
    const arrowSource = root?.$flightRouteArrowLayer.getSource() ?? undefined
    if (!arrowSource) return
    arrowSource.clear()
    const angleDeg = state.directionDeg
    const lineFeature = new Feature({ geometry: new LineString([start, end]) })
    lineFeature.setStyle([
      new Style({
        stroke: new Stroke({
          color: '#D28519',
          width: 3
        })
      }),
      // new Style({
      //   geometry: new Point(end),
      //   image: new RegularShape({
      //     points: 3,
      //     radius: 10,
      // rotation: angleDeg,
      //     fill: new Fill({ color: '#D28519' })
      //   })
      // })
    ])
    arrowSource.addFeature(lineFeature)
  }

  // set arrow on the center
  function drawArrowToCenter () {
    if (!state.polygonFeature) return

    const polygon = state.polygonFeature.getGeometry()
    const directionDeg = state.directionDeg
    const angleRad = directionDeg * 180 / Math.PI
    const center = polygon.getInteriorPoint().getCoordinates()

    const length = 1e7 // long enough to exceed polygon size

    const rayEnd = [
      center[0] + Math.cos(angleRad) * length,
      center[1] + Math.sin(angleRad) * length,
    ]

    const ring = polygon.getCoordinates()[0] // outer ring

    let minDistance = Infinity

    for (let i = 0; i < ring.length - 1; i++) {
      const p = lineIntersection(
        center,
        rayEnd,
        ring[i],
        ring[i + 1]
      )

      if (!p) continue

      const dx = p[0] - center[0]
      const dy = p[1] - center[1]

      // Must be forward along ray
      if (dx * Math.cos(angleRad) + dy * Math.sin(angleRad) <= 0) continue

      const dist = Math.sqrt(dx * dx + dy * dy)
      minDistance = Math.min(minDistance, dist)
    }

    const directionArrowLength = minDistance + 100
    const [start, end] = createArrowPoints(center, angleRad, directionArrowLength)
    drawArrowFeature(start, end)
  }

  function drawRoute (flightHeightMode: string, polygon: Polygon, directionDeg:number, bearingDeg: number, flightAltitude:number) {
    if (state.routeLayer) {
      state.routeLayer?.getSource().clear()
      root?.$map.removeLayer(state.routeLayer)
    }

    const routeSource = new VectorSource()
    const routeLayer = new VectorLayer({ source: routeSource })
    root?.$map.addLayer(routeLayer)
    state.routeLayer = routeLayer

    const routeBearing = useLawnmower.generateLawnmowerPath(flightHeightMode, polygon, directionDeg, bearingDeg, flightAltitude)
    const routeWithBearing = routeBearing.routePointBearings
    state.routeBearingData = routeBearing
    console.log(state.routeBearingData)

    // Draw points
    routeWithBearing.forEach((wp, i) => {
      const ptindex = wp.index + 1
      const ptFeature = new Feature({ geometry: new Point(wp.coordinate), waypointIndex: ptindex, heading: wp.bearing })
      const bearingRad = wp.bearing * Math.PI / 180
      ptFeature.setStyle([
        new Style({
          image: new CircleStyle({
            radius: 3,
            fill: new Fill({ color: 'orange' }),
            stroke: new Stroke({ color: 'white', width: 1 })
          })
        }),
        // arrow
        new Style({
          image: new Icon({
            src: arrowIcon,
            rotation: bearingRad, // radians
            rotateWithView: true,
            scale: 0.2
          })
        }),
        // index text
        new Style({
          text: new Text({
            // text: String(ptindex) + ' (' + String(wp.altitude.toFixed(2)) + ', ' + String(wp.bearing.toFixed(2)) + ')',
            text: String(ptindex) + ' (' + String(Math.round(wp.altitude)) + ')',
            font: 'bold 11px sans-serif',
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#000', width: 2 }),
            textAlign: 'center',
            textBaseline: 'middle',
            offsetY: -8
          })
        })
      ])
      routeSource.addFeature(ptFeature)
    })

    // Draw line
    if (routeWithBearing.length > 1) {
      const line = new Feature({
        geometry: new LineString(
          routeWithBearing.map(wp => wp.coordinate),
        )
      })
      routeSource.addFeature(line)
    }
  }

  // --- Update route visualization ---
  function updateRoute () {
    if (!state.polygonFeature) return

    const polygon = state.polygonFeature.getGeometry()
    const bearingDeg = state.bearingDeg
    const directionDeg = state.directionDeg
    const routeLineDeg = directionDeg
    const flightHeightMode = state.flightHeightMode
    const flightAltitude = state.flightAltitude
    drawRoute(flightHeightMode, polygon, routeLineDeg, bearingDeg, flightAltitude)
  }

  // // --- Visualization ---
  // function visualizeRoute (feature) {
  //   // if(!polygonFeature) return alert("Draw a polygon first!");
  //   const poly = feature.getGeometry() as Polygon
  //   const routePoints = generateLawnmower(poly)
  //   console.log('Generated route points:', routePoints)

  //   const features = routePoints.map((p, i) => new Feature({ geometry: new Point(p), waypointIndex: i + 1 }))
  //   const line = new Feature({ geometry: new LineString(routePoints) })

  //   features.forEach(f => f.setStyle(new Style({ image: new CircleStyle({ radius: 5, fill: new Fill({ color: 'red' }), stroke: new Stroke({ color: 'white', width: 2 }) }) })))
  //   line.setStyle(new Style({ stroke: new Stroke({ color: 'blue', width: 2 }) }))

  //   if (state.routeLayer) root?.$map.removeLayer(state.routeLayer)
  //   const routeLayer = new VectorLayer({ source: new VectorSource({ features: [line, ...features] }) })
  //   root?.$map.addLayer(routeLayer)
  //   state.routeLayer = routeLayer

  //   // Store route globally for JSON export
  //   // (window as any).routePoints = routePoints
  // }

  function setDragPanActive (active: boolean) {
    root?.$map.getInteractions().forEach((interaction) => {
      if (interaction instanceof DragPan) {
        interaction.setActive(active)
      }
    })
  }

  function enableModify () {
    const flightRouteBoundaryLayerSource = root?.$flightRouteBoundaryLayer.getSource()
    if (!flightRouteBoundaryLayerSource) return

    // route direction modify
    if (state.directionArrowModify) {
      root?.$map.removeInteraction(state.directionArrowModify)
    }

    const directionArrowModify = new Modify({
      source: root?.$flightRouteArrowLayer.getSource() ?? undefined,
      condition: e => {
        return e.originalEvent.buttons === 1
      },
      // Disable inserting new vertices
      insertVertexCondition: () => false,
    })
    // Prevent inserting new vertices
    // directionArrowModify.set('insertVertexCondition', () => false)

    directionArrowModify.on('modifyend', (e) => {
      e.features.forEach((feature: Feature) => {
        const geom = feature.getGeometry() as LineString
        const [start, end] = geom.getCoordinates()

        const dx = end[0] - start[0]
        const dy = end[1] - start[1]
        const length = Math.hypot(dx, dy)

        const angleRad = Math.atan2(dy, dx)
        const angleDeg = angleRad * 180 / Math.PI

        state.directionArrowLength = length
        state.directionDeg = angleDeg

        drawArrowFeature(start, end)
        updateRoute()
      })
    })
    state.directionArrowModify = directionArrowModify
    root?.$map.addInteraction(directionArrowModify)

    // boundary Polygon modify
    if (state.boundaryModify) {
      root?.$map.removeInteraction(state.boundaryModify)
    }

    const contourSource = root?.$contourLineLayer.getSource() ?? undefined

    const boundaryModify = new Modify({
      source: flightRouteBoundaryLayerSource,
      trace: true,
      traceSource: contourSource,
      hitTolerance: 15, // 🔥 REQUIRED
      style: new Style({
        image: new CircleStyle({
          radius: 8, // BIG hit area
          fill: new Fill({ color: 'white' }),
          stroke: new Stroke({
            color: 'blue',
            width: 2,
          }),
        }),
      }),
    })

    boundaryModify.on('modifyend', e => updateRoute())
    state.boundaryModify = boundaryModify

    root?.$map.addInteraction(boundaryModify)
  }

  function addInteraction (type: any, geometryFunction, getDrawCallback:Function) {
    if (state.draw) {
      state.draw.setActive(false)
      root?.$map.removeInteraction(state.draw)
    }

    enableModify()

    const drawSource = root?.$flightRouteBoundaryLayer.getSource() ?? undefined
    const contourSource = root?.$contourLineLayer.getSource() ?? undefined
    const draw = new Draw({
      source: drawSource,
      type: type,
      trace: true,
      traceSource: contourSource,
      geometryFunction: geometryFunction,
    })

    state.draw = draw
    root?.$map.addInteraction(draw)

    const snap = new Snap({ source: drawSource })
    state.snap = snap
    root?.$map.addInteraction(snap)

    const contourSnap = new Snap({ source: contourSource })
    root?.$map.addInteraction(contourSnap)

    root?.$map.on('drawend', getDrawCallback)

    // 🔥 IMPORTANT: only once
    draw.on('drawend', (e) => {
      const feature = e.feature
      state.polygonFeature = feature
      drawArrowToCenter()
      updateRoute()
    })
  }

  function drawClear () {
    root?.$flightRouteBoundaryLayer.getSource().clear()
    root?.$flightRouteArrowLayer.getSource().clear()
    state.routeLayer.getSource().clear()
  }

  function drawSave () {
    console.log(state.routeBearingData)
    if (state.routeBearingData !== null) {
      buildDock2MissionKmz(state.routeBearingData)
    }
  }

  function drawPin (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('Point', undefined, getDrawCallback)
    // addInteraction('Circle', createRegularPolygon(4), getDrawCallback)
    // root?.$mouseTool = new Draw({
    //   source: root?.$flightRouteBoundaryLayer.getSource() ?? undefined,
    //   type: type,
    // })
    // root?.$map.on('drawend', getDrawCallback)
    // root?.$mouseTool.marker({
    //   title: type + state.pinNum,
    //   icon: pin2d8cf0,
    // })
    // state.pinNum++
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolyline (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('MultiLineString', undefined, getDrawCallback)
    // root?.$mouseTool.polyline({
    //   strokeColor: '#2d8cf0',
    //   strokeOpacity: 1,
    //   strokeWeight: 2,
    //   strokeStyle: 'solid',
    //   title: type + state.polylineNum++
    // })
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawPolygon (type:MapDoodleType, getDrawCallback:Function) {
    addInteraction('Polygon', undefined, getDrawCallback)
    // root?.$mouseTool.polygon({
    //   strokeColor: '#2d8cf0',
    //   strokeOpacity: 1,
    //   strokeWeight: 2,
    //   fillColor: '#1791fc',
    //   fillOpacity: 0.4,
    //   title: type + state.PolygonNum++
    // })
    // root?.$mouseTool.on('draw', getDrawCallback)
  }

  function drawOff (type:MapDoodleType) {
    state.boundaryModify?.setActive(false)
    state.snap?.setActive(false)
    state.draw?.setActive(false)
    // setDragPanActive(true)
    // root?.$mouseTool.close()
    // root?.$mouseTool.off('draw')
  }

  function drawFlightAreaPolygon (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    addInteraction('Polygon', undefined, getDrawFlightAreaCallback)
    // root?.$mouseTool.polygon({
    //   strokeColor: flightAreaColorMap[type],
    //   strokeOpacity: 1,
    //   strokeWeight: 4,
    //   extData: {
    //     type: type,
    //     mapType: 'polygon',
    //   },
    //   strokeStyle: 'dashed',
    //   strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
    //   fillColor: flightAreaColorMap[type],
    //   fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    // })
    // root?.$mouseTool.on('draw', getDrawFlightAreaCallback)
  }

  function drawFlightAreaCircle (type: EFlightAreaType, getDrawFlightAreaCallback: Function) {
    addInteraction('Circle', undefined, getDrawFlightAreaCallback)
    // root?.$mouseTool.circle({
    //   strokeColor: flightAreaColorMap[type],
    //   strokeOpacity: 1,
    //   strokeWeight: 6,
    //   extData: {
    //     type: type,
    //     mapType: 'circle',
    //   },
    //   strokeStyle: 'dashed',
    //   strokeDasharray: EFlightAreaType.NFZ === type ? [10, 2] : [10, 1, 2],
    //   fillColor: flightAreaColorMap[type],
    //   fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
    // })
    // root?.$mouseTool.on('draw', getDrawFlightAreaCallback)
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    state.currentType = type
    if (flightAreaType) {
      switch (type) {
        case MapDoodleEnum.POLYGON:
          drawFlightAreaPolygon(flightAreaType, getDrawCallback)
          return
        case MapDoodleEnum.CIRCLE:
          drawFlightAreaCircle(flightAreaType, getDrawCallback)
          return
        default:
          message.error(`Invalid type: ${flightAreaType}`)
          return
      }
    }
    switch (type) {
      case MapDoodleEnum.PIN:
        drawPin(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        drawPolyline(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        drawPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.CLEAR:
        drawClear()
        break
      case MapDoodleEnum.SAVE:
        drawSave()
        break
      case MapDoodleEnum.Close:
        drawOff(type)
        break
    }
  }

  return {
    mouseTool
  }
}
